import React, { useEffect } from 'react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTab: string;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, currentTab }) => {
  // Handle ESC key press
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Handle click outside modal
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('modal-overlay')) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getHelpContent = () => {
    switch (currentTab) {
      case 'company-details':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              The purpose of this screen is to record the <strong>Company Profile</strong> and <strong>Relationship Summary</strong>.
            </p>

            <hr className="my-3" />

            <h4 className="text-lg font-semibold mb-2">Company Profile</h4>
            <p className="w-full">
              Record detailed information about the company including their industry, business model, and key characteristics. This information helps us understand the customer's business context and tailor our recommendations accordingly.
            </p>

            <hr className="my-3" />

            <h4 className="text-lg font-semibold mb-2">Relationship Summary</h4>
            <p className="w-full">
              Document the current relationship between Presidio and the customer, including engagement history,
              key contacts, and partnership status. This helps maintain continuity and provides context for future interactions.
            </p>
          </div>
        );

      case 'goals':
      case 'key_goals':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              The purpose of this screen is to record the high-level <strong>Goals</strong>,
              <strong> Priorities</strong> and <strong>Industry Trends</strong> of the customer.
            </p>

            <hr className="my-3" />

            <h4 className="text-lg font-semibold mb-2">Goals</h4>
            <p className="w-full">
              Goals are collected from your contacts at the company, their published documentation, and meetings with
              the customer.
              These goals will later be linked with projects to understand how well the customer's intentions align with
              execution.
            </p>

            <hr className="my-3" />

            <h4 className="text-lg font-semibold mb-2">Priorities</h4>
            <p className="w-full">
              Priorities represent broader themes or concepts the customer is focused on — not necessarily specific
              goals,
              but guiding ideas they are working toward.
            </p>

            <hr className="my-3" />

            <h4 className="text-lg font-semibold mb-2">Industry Trends</h4>
            <p className="w-full">
              Use this section to record your research into the customer's industry.
              This will help frame conversations and respond to questions like:
              <em>"What are other companies in my industry doing?"</em>
            </p>
          </div>
        );

      case 'projects':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              The purpose of this screen is to record the high-level <strong>Projects</strong> of the customer.
            </p>
            <p className="w-full">
              The idea behind this tab is to do 3 things:
            </p>
            <ul className="list-disc pl-5 mb-4">
              <li>First, record all the projects that we know about that the customer is executing.</li>
              <li>Secondly, associate those projects with the goals from the first tab that each project is helping
                achieve.</li>
              <li>Third, associate the projects to the Presidio Capabilities from our lines of business.</li>
              <li>The goals and Capabilities are both <strong>multi-select</strong> fields, so select all that apply.
              </li>
            </ul>

            <hr className="my-3" />
            <h4 className="text-lg font-semibold mb-2">Modernization Plan Projects</h4>
            <p className="w-full">
              When we are working on the Modernization Plan for a customer it will be important to be able to create <em>virtual </em>
              projects that do not currently exist, but are needed to close the gaps in the customer's plans to achieve
              their goals. 
              To create these types of projects choose 'Yes' to the 'Modernizaion Plan Projects' checkbox. 
              These projects will represent the Modernization Plan.
            </p>
          </div>
        );

      case 'technology':
      case 'technology_alignment':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              The purpose of this tab is to record the <strong>Technology</strong> the customer is currently using.
              We do this so we can present solutions that fit better into the customer's environment.
            </p>
            <p className="w-full">
              The Technology entry form is designed to help build structured data that can be compared across customers.
              To do that we use a waterfall type data entry.
              First choose the <em>Area</em> of Technology, then choose the <em>Specialty</em> within that area, and
              then choose a vendor from the vendor list.
            </p>
          </div>
        );

      case 'applications':
      case 'critical_application':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              The <em>Applications</em> tab is about collecting data about the applications that power the customer's
              business. In order for us to understand the customer
              and assist them in modernizing their environment we must understand what the customer depends on. It is
              key for us to understand their dependencies on
              applications and know where those applications run (on-prem, in cloud, Saas, etc).
            </p>
            <p className="w-full">
              Enter the information you know about every app they run. The apps could be infrastructure apps or business
              apps.
              The more information you record on this tab the more we can build off of later.
            </p>
          </div>
        );

      case 'transformation':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              The <em>Transformation Maturity</em> tab is about collecting data about what we perceive the customer is
              capable of executing.
            </p>
            <p className="w-full">
              The idea is that the <em>Art of the Possible</em> tab lists a large number of activities the customer
              COULD undertake to improve their environment.
              Our challenge is to highlight those that we believe the customer is either trying to do, or has plans to
              do in the future.
            </p>
            <p className="w-full">
              Expand an <em>Area</em>, and then edit a row and record the perceived Business and Technical impacts of
              executing this initiative.
              You can also rank these initiatives in order per area.
            </p>
          </div>
        );

      case 'art-of-the-possible':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              The <em>Art of the Possible</em> tab is about collecting data about what we perceive the customer is
              capable of executing.
            </p>
            <p className="w-full">
              The idea is that the <em>Art of the Possible</em> tab lists a large number of activities the customer
              COULD undertake to improve their environment.
              Our challenge is to highlight those that we believe the customer is either trying to do,<br></br>
              or has plans to do in the future.
            </p>
            <hr className="my-3" />
            <h4 className="text-lg font-semibold mb-2">To Use</h4>
            <p className="w-full">
              Expand an <em>Area</em>, and then edit a row and record the perceived Business and Technical impacts of
              executing <br></br> this initiative.
              You can also rank these initiatives in order per area.
            </p>
          </div>
        );

      case 'frameworks':
      case 'business_frameworks':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              The <em>Frameworks</em> tab is still under construction. The purpose of this tab is to collect information
              about the operational frameworks and
              compliance regulations the customer has. Do they have Hipaa regulations, PCI, etc ?
            </p>
          </div>
        );

      case 'project-output':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              This is the output tab where you can view the generated reports and visualizations based on the data collected in other tabs.
            </p>
          </div>
        );

      case 'transformation-graph':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              This tab shows the transformation maturity graph visualization based on the data collected in the transformation tab.
            </p>
          </div>
        );

      case 'customer-dashboard':
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              This is the customer dashboard overview tab where you can see a summary of all the customer's data and navigate to different sections.
            </p>
          </div>
        );

      default:
        return (
          <div className="text-left w-full">
            <p className="mb-4 w-full">
              Help content for this tab is not available yet.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-end z-50 modal-overlay">
      <div className="bg-white h-full w-full sm:w-[500px] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Help</h2>
          <button 
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100 focus:outline-none transition-colors"
            type="button"
          >
            <svg className="h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="w-full max-w-none">
            {getHelpContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
